// Переписка ученика со взрослыми (docs/25-tutor-feedback.md): кнопка в шапке
// и окно с нитями.
//
// **Кнопки нет, пока нет нити.** Нить заводит взрослый; до его первого
// сообщения писать некому, и пустой значок в шапке был бы приглашением в
// никуда.
//
// **Прочтение ставится открытием нити.** Ученику не показывается, прочитал ли
// взрослый его ответ: отметка «прочитано» — инструмент взрослого, а у
// пятнадцатилетнего она превращается в ожидание.
//
// **Тон.** Число непрочитанных — числом, без точек-тревог и восклицаний.

import { useEffect, useRef, useState, type ReactNode } from "react";
import type { FunctionReturnType } from "convex/server";
import { api, useMutation, useQuery } from "../api";
import type { Id } from "../../convex/_generated/dataModel";
import { formatDay } from "../lib/dates";
import { reasonOf } from "../lib/errors";
import { when } from "../lib/feedback";
import { Formula } from "../lib/Formula";
import { SECTION_LABEL } from "../lib/sections";
import { IconArrow, IconCross, IconMessage } from "./Icons";
import "../feedback.css";

export type Anchor = NonNullable<
  FunctionReturnType<typeof api.messages.thread>["messages"][number]["anchor"]
>;

/**
 * К чему сообщение: задача, отметка «неясно» или день. Снимок на момент
 * отправки — задачу могли убрать из пула, а сообщение читается, как было
 * написано. Эталона в снимке нет.
 */
export function AnchorView({ anchor }: { anchor: Anchor }) {
  if (anchor.kind === "day") {
    return (
      <div className="msg-anchor">
        <span className="card__label">День · {formatDay(anchor.date)}</span>{" "}
        <span className="muted">
          решено <span className="num">{anchor.done}</span> из <span className="num">{anchor.total}</span> ·
          верно <span className="num">{anchor.correct}</span> · около{" "}
          <span className="num">{anchor.minutes}</span> мин
        </span>
      </div>
    );
  }
  if (anchor.kind === "item") {
    return (
      <div className="msg-anchor">
        <div className="card__label">Задача · {anchor.skillName}</div>
        {anchor.statement === null ? null : (
          <div className="msg-anchor__statement">
            <Formula source={anchor.statement} />
          </div>
        )}
        <div className="msg-anchor__answer">
          <span className="muted">ответ</span>{" "}
          {anchor.choiceText !== undefined ? (
            <Formula source={anchor.choiceText} />
          ) : (
            <span className="msg-anchor__raw">{anchor.raw === "" ? "пусто" : anchor.raw}</span>
          )}{" "}
          <span className={anchor.correct ? "msg-anchor__ok" : "msg-anchor__miss"}>
            {anchor.correct ? "верно" : "неверно"}
          </span>
        </div>
      </div>
    );
  }
  return (
    <div className="msg-anchor">
      <div className="card__label">
        Неясно · {anchor.skillName}
        {anchor.target === "step" && anchor.steps !== undefined && anchor.steps.length > 0
          ? ` · ${anchor.steps.length === 1 ? "шаг" : "шаги"} ${anchor.steps.map((s) => s + 1).join(", ")}`
          : ""}
      </div>
      {anchor.target === "step" && anchor.statement !== undefined && anchor.statement !== null ? (
        <div className="msg-anchor__statement">
          <Formula source={anchor.statement} />
        </div>
      ) : null}
      {anchor.target === "theory" && anchor.sections !== undefined ? (
        <div className="muted">
          разбор темы: {anchor.sections.map((s) => SECTION_LABEL[s.section]).join(", ")}
        </div>
      ) : null}
    </div>
  );
}

/** Одна реплика нити: кто, когда, к чему и текст. */
export function MessageTurn({
  who,
  at,
  text,
  anchor,
  mine,
  tail,
}: {
  who: string;
  at: number;
  text: string;
  anchor: Anchor | null;
  mine: boolean;
  tail?: ReactNode;
}) {
  return (
    <div className={`msg-turn${mine ? " msg-turn--mine" : ""}`}>
      <div className="card__label">
        {who} · {when(at)}
        {tail ?? null}
      </div>
      {anchor === null ? null : <AnchorView anchor={anchor} />}
      <p className="fb-turn__text">{text}</p>
    </div>
  );
}

/** Нить глазами ученика. Открыл — прочитал; новые, пришедшие при открытом окне, — тоже. */
function LearnerThread({ learnerKey, tutorId }: { learnerKey: string; tutorId: Id<"tutors"> }) {
  const thread = useQuery(api.messages.thread, { learnerKey, tutorId });
  const read = useMutation(api.messages.read);
  const reply = useMutation(api.messages.reply);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const end = useRef<HTMLDivElement>(null);

  const unread = thread?.unread ?? 0;
  useEffect(() => {
    if (unread > 0) void read({ learnerKey, tutorId }).catch(() => undefined);
  }, [unread, learnerKey, tutorId, read]);
  const count = thread?.messages.length ?? 0;
  // Тело в скобках, а не выражением: в новых Chrome (проверено на Chromium
  // 153) `scrollIntoView` возвращает Promise, React принимает его за функцию
  // очистки и на закрытии окна или новом сообщении падает «destroy is not a
  // function» (замечание ученика №4, 30 сентября 2026).
  useEffect(() => {
    end.current?.scrollIntoView({ block: "end" });
  }, [count]);

  if (thread === undefined) return <div className="skeleton" style={{ height: 96 }} />;

  const send = () => {
    if (text.trim() === "") return;
    setBusy(true);
    setError(null);
    void reply({ learnerKey, tutorId, text })
      .then(() => setText(""))
      .catch((e: unknown) => setError(reasonOf(e)))
      .finally(() => setBusy(false));
  };

  return (
    <div className="msg-thread">
      <div className="fb-turns">
        {thread.messages.map((m) => (
          <MessageTurn
            key={m.id}
            who={m.from === "tutor" ? thread.name : "Вы"}
            at={m.at}
            text={m.text}
            anchor={m.anchor}
            mine={m.from === "learner"}
          />
        ))}
        <div ref={end} />
      </div>
      <div className="fb-reply">
        <textarea
          className="field fb-text"
          rows={3}
          value={text}
          placeholder="Ответить"
          aria-label={`Ответ: ${thread.name}`}
          onChange={(e) => setText(e.target.value)}
        />
        {error === null ? null : <div className="fb-error">{error}</div>}
        <div className="fb-actions">
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy || text.trim() === ""}
            onClick={send}
          >
            Отправить
          </button>
        </div>
      </div>
    </div>
  );
}

export function Messages({ learnerKey }: { learnerKey: string }) {
  const badge = useQuery(api.messages.unread, { learnerKey });
  const [open, setOpen] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const d = dialog.current;
    if (open && d !== null && !d.open) d.showModal();
  }, [open]);

  if (badge === undefined || badge.threads === 0) return null;
  const label = badge.unread > 0 ? `Сообщения — непрочитанных ${badge.unread}` : "Сообщения";

  return (
    <>
      <button
        type="button"
        className="appnav__btn fb-btn"
        title={label}
        aria-label={label}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        <IconMessage size={19} />
        {badge.unread > 0 ? <span className="msg-btn__count num">{badge.unread}</span> : null}
      </button>
      {open ? (
        <dialog
          ref={dialog}
          className="fb-dialog"
          aria-labelledby="msg-title"
          onClose={() => setOpen(false)}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) dialog.current?.close();
          }}
        >
          <MessagesBody learnerKey={learnerKey} onClose={() => dialog.current?.close()} />
        </dialog>
      ) : null}
    </>
  );
}

/** Окно: одна нить — сразу она, несколько — список, из которого открывается нужная. */
function MessagesBody({ learnerKey, onClose }: { learnerKey: string; onClose: () => void }) {
  const threads = useQuery(api.messages.threads, { learnerKey });
  const [picked, setPicked] = useState<Id<"tutors"> | null>(null);
  const only = threads !== undefined && threads.length === 1 ? threads[0]!.tutorId : null;
  const at = picked ?? only;
  const current = threads?.find((t) => t.tutorId === at);

  return (
    <div className="fb-frame">
      <div className="fb-head">
        <div className="row" style={{ gap: 8 }}>
          {picked !== null && only === null ? (
            <button
              type="button"
              className="fb-close msg-back"
              onClick={() => setPicked(null)}
              aria-label="К списку переписок"
              title="К списку переписок"
            >
              <IconArrow size={16} />
            </button>
          ) : null}
          <h2 id="msg-title" className="fb-title">
            {current === undefined ? "Сообщения" : current.name}
          </h2>
        </div>
        <button type="button" className="fb-close" onClick={onClose} aria-label="Закрыть">
          <IconCross size={16} />
        </button>
      </div>
      <div className="fb-body">
        {threads === undefined ? (
          <div className="skeleton" style={{ height: 96 }} />
        ) : at === null ? (
          <ul className="msg-list">
            {threads.map((t) => (
              <li key={t.tutorId}>
                <button type="button" className="msg-list__row" onClick={() => setPicked(t.tutorId)}>
                  <span className="msg-list__name">{t.name}</span>
                  <span className="faint">{when(t.lastAt)}</span>
                  {t.unread > 0 ? <span className="fb-tab__count num">{t.unread}</span> : null}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <LearnerThread learnerKey={learnerKey} tutorId={at} />
        )}
      </div>
    </div>
  );
}

/**
 * Комментарии взрослых к задаче — в «Истории» под разбором. Те же сообщения,
 * что в нити, подписанные именем.
 */
export function ItemComments({ learnerKey, itemKey }: { learnerKey: string; itemKey: string }) {
  const rows = useQuery(api.messages.forItem, { learnerKey, itemKey });
  if (rows === undefined || rows.length === 0) return null;
  return (
    <div className="msg-comments">
      {rows.map((c) => (
        <div key={c.id} className="msg-comment">
          <div className="card__label">
            {c.name} · {when(c.at)}
          </div>
          <p className="fb-turn__text">{c.text}</p>
        </div>
      ))}
    </div>
  );
}
